import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, PageHeader, SkeletonRows, StatusBadge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { labelFor } from '@/shared/lib/labels';
import { agentApi, type DialerState } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';

/**
 * "הקמפיינים שלי" — מסך הנחיתה של הנציג (08/09/2026, דורון: "שנציג נכנס
 * ומתחבר צריך להופיע לו הכרטיסיות עם הקמפיינים שהוא משויך אליו").
 *
 * עד עכשיו הקמפיינים היו קבורים במסך `/agent/dialer` שהוסתר מהתפריט, והנציג
 * נחת על שיחות המעקב — כך שהוא לא ראה בכלל לאילו קמפיינים הוא משויך.
 *
 * כל קמפיין = כרטיס אחד עם: מצב (פעיל / מושהה / מחוץ לשעות), סוג החיוג,
 * כמה לידים ממתינים וכמה משויכים לו, וכפתור פעולה אחד ברור.
 */
interface AgentCampaign {
  id: number;
  name: string;
  status: string;
  dialer_mode: string;
  in_window?: boolean;
  window_today?: { open: string; close: string } | null;
  leads_total?: number;
  leads_queued?: number;
  leads_mine?: number;
}

/** תיאור אנושי לסוג החיוג — "מתקדם"/"progressive" לא אומר לנציג כלום. */
const MODE_HINT: Record<string, string> = {
  preview: 'אתה מתחיל כל סבב חיוג ידנית',
  progressive: 'המערכת מחייגת עבורך ליד אחרי ליד',
  predictive: 'המערכת מחייגת מראש ומחברת אותך למי שעונה',
  manual: 'חיוג ידני בלבד',
};

export function MyCampaigns() {
  const [campaigns, setCampaigns] = useState<AgentCampaign[]>([]);
  const [state, setState] = useState<DialerState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    void Promise.all([
      agentApi.dialerCampaigns().catch(() => []),
      agentApi.dialerState().catch(() => null),
    ]).then(([cs, st]) => {
      setCampaigns(cs as AgentCampaign[]);
      setState(st);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  /** מצב התותח פר-קמפיין מגיע מ-dialerState (auto/paused), לא מרשימת הקמפיינים. */
  const dialerFor = (id: number) => (state?.campaigns ?? []).find((c) => c.id === id);

  async function resume(campaignId: number) {
    setBusy(campaignId);
    try {
      await agentApi.dialerPause(campaignId, false);
      toast('החיוג האוטומטי חודש', 'ok');
      load();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <div className="stack" style={{ padding: 'var(--gap)' }}><Card><SkeletonRows rows={3} /></Card></div>;
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="הקמפיינים שלי" subtitle="הקמפיינים שאתה משויך אליהם. בחר קמפיין כדי להתחיל לחייג." />

      {campaigns.length === 0 ? (
        <EmptyState
          icon="target"
          title="אינך משויך לאף קמפיין"
          description="מנהל המוקד משייך נציגים לקמפיינים. עד אז אפשר לעבוד משיחות המעקב ומהלידים שבטיפולך."
          action={<button type="button" className="btn" onClick={() => navigate('/agent/callbacks')}>לשיחות המעקב</button>}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--gap)' }}>
          {campaigns.map((c) => {
            const d = dialerFor(c.id);
            const outside = c.in_window === false;
            const auto = !!d?.auto;
            const paused = !!d?.paused;
            const running = auto && !paused && !outside;
            const queued = c.leads_queued ?? 0;

            return (
              <Card key={c.id}>
                <div className="stack" style={{ gap: 12 }}>
                  {/* שם + מצב */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--t-lg)', fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
                        {MODE_HINT[c.dialer_mode] ?? labelFor('dialer_mode', c.dialer_mode)}
                      </div>
                    </div>
                    {running
                      ? <StatusBadge tone="ok" size="sm" dot>מחייג</StatusBadge>
                      : outside
                        ? <StatusBadge tone="warn" size="sm">מחוץ לשעות</StatusBadge>
                        : paused
                          ? <StatusBadge tone="warn" size="sm">מושהה</StatusBadge>
                          : <StatusBadge tone="neutral" size="sm">מוכן</StatusBadge>}
                  </div>

                  {/* מונים — כמה עבודה יש כאן */}
                  <div style={{ display: 'flex', gap: 18 }}>
                    <div>
                      <div dir="ltr" style={{ fontSize: 'var(--t-xl)', fontWeight: 700, textAlign: 'start' }}>{queued}</div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>ממתינים לחיוג</div>
                    </div>
                    {(c.leads_mine ?? 0) > 0 && (
                      <div>
                        <div dir="ltr" style={{ fontSize: 'var(--t-xl)', fontWeight: 700, textAlign: 'start' }}>{c.leads_mine}</div>
                        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>משויכים לי</div>
                      </div>
                    )}
                  </div>

                  {outside && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-xs)', color: 'var(--warn)' }}>
                      <Icon name="clock" size={13} />
                      {c.window_today
                        ? `חיוג יתאפשר בין ${c.window_today.open} ל-${c.window_today.close}`
                        : 'אין חיוג היום'}
                    </div>
                  )}

                  {/* פעולה אחת ברורה */}
                  {paused && auto ? (
                    <button type="button" className="btn primary" disabled={busy === c.id} onClick={() => void resume(c.id)}>
                      <Icon name="play" size={14} /> חדש חיוג
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn primary"
                      disabled={queued === 0}
                      title={queued === 0 ? 'אין לידים ממתינים בקמפיין' : outside ? 'מחוץ לשעות הפעילות — תוצג הודעה והחיוג ימשיך' : undefined}
                      onClick={() => navigate(`/agent/campaign?campaign=${c.id}`)}
                    >
                      <Icon name="phoneOut" size={14} /> {running ? 'למסך החיוג' : 'התחל לחייג'}
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
